import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Target,
  CheckCircle,
  AlertCircle,
  Clock,
  Edit,
  Download,
  Share,
  Star,
  Activity,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { usePerformanceCycle } from "../../../hooks/usePerformanceCycles";
import { useGoalsByCycle } from "../../../hooks/useGoals";

const PerformanceCycleDetail = ({ cycleId: propCycleId, onClose, isDrawer }) => {
  const { cycleId: paramCycleId } = useParams();
  const cycleId = propCycleId || paramCycleId;
  const navigate = useNavigate();
  const location = useLocation();
  const [cycle, setCycle] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // React Query hook for fetching cycle data
  const {
    data: cycleData,
    isLoading,
    isError,
    error,
  } = usePerformanceCycle(cycleId);

  // React Query hook for fetching goals for this cycle
  const {
    data: goalsData,
    isLoading: goalsLoading,
    isError: goalsError,
  } = useGoalsByCycle(cycleId);

  useEffect(() => {
    if (cycleData && cycleData.id) {
      console.log("📊 PerformanceCycleDetail - Received cycleData:", cycleData);
      console.log(
        "📊 PerformanceCycleDetail - Population data:",
        cycleData.population
      );
      console.log(
        "📊 PerformanceCycleDetail - Metrics data:",
        cycleData.metrics
      );

      // Transform API data to match UI expectations
      const transformedCycle = {
        id: cycleData.id,
        name: cycleData.cycleName,
        description: cycleData.description,
        startDate: cycleData.startDate ? cycleData.startDate.split("T")[0] : "",
        endDate: cycleData.endDate ? cycleData.endDate.split("T")[0] : "",
        status: cycleData.status,
        // Use population data or calculate from other sources
        totalEmployees:
          cycleData.population?.totalEnrolled ||
          cycleData.population?.totalEligible ||
          0,
        completedReviews: cycleData.metrics?.completedReviews || 0,
        pendingReviews: Math.max(
          0,
          (cycleData.population?.totalEnrolled ||
            cycleData.population?.totalEligible ||
            0) - (cycleData.metrics?.completedReviews || 0)
        ),
        averageRating: cycleData.metrics?.averageRating || 0,
        stages: cycleData.stages
          ? cycleData.stages.map((stage, index) => {
            const totalParticipants =
              cycleData.population?.totalEnrolled ||
              cycleData.population?.totalEligible ||
              0;
            return {
              name: stage.name
                .replace("_", " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()),
              status: stage.isCompleted
                ? "completed"
                : index === 0
                  ? "in-progress"
                  : "pending",
              participants: totalParticipants,
              completed: stage.isCompleted
                ? totalParticipants
                : index === 0
                  ? Math.floor(totalParticipants * 0.8)
                  : 0,
            };
          })
          : [],
        departments: [
          { name: "Sales", total: 45, completed: 40, average: 4.3 },
          { name: "Marketing", total: 25, completed: 20, average: 4.1 },
          { name: "Engineering", total: 35, completed: 30, average: 4.4 },
          { name: "HR", total: 15, completed: 15, average: 4.0 },
          { name: "Finance", total: 20, completed: 15, average: 4.2 },
        ],
        recentActivity: [
          {
            id: 1,
            action: "Review completed",
            employee: "John Smith",
            manager: "Sarah Johnson",
            timestamp: "2024-12-15 14:30",
          },
          {
            id: 2,
            action: "Calibration started",
            employee: "Marketing Team",
            manager: "Mike Davis",
            timestamp: "2024-12-15 10:15",
          },
          {
            id: 3,
            action: "Self review submitted",
            employee: "Alice Brown",
            manager: "Tom Wilson",
            timestamp: "2024-12-14 16:45",
          },
        ],
        goals: goalsData?.data || goalsData || [], // Real goals data from API
      };

      console.log(
        "📊 PerformanceCycleDetail - Transformed cycle:",
        transformedCycle
      );
      setCycle(transformedCycle);
    }
  }, [cycleData, goalsData]);

  const handleBack = () => {
    if (isDrawer) {
      onClose?.();
    } else {
      navigate(-1);
    }
  };

  const handleEditCycle = () => {
    if (isDrawer) {
      const url = new URL(window.location.href);
      url.searchParams.set('action', 'editCycle');
      url.searchParams.set('id', cycleId);
      window.history.pushState({}, '', url);
      // Since it's in a drawer, the parent (PerformanceModule) needs to re-render 
      // or we just need to change the state if we pass down a setter.
      // But actually PerformanceModule listens to URL changes via useSearchParams.
      // So setting window.location or using a hook is better.
    } else {
      navigate(`/performance_cycle_detail/${cycleId}/edit`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "text-green-600 bg-green-100";
      case "in-progress":
        return "text-[#0F2942] bg-blue-100";
      case "pending":
        return "text-gray-600 bg-gray-100";
      case "at-risk":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4" />;
      case "in-progress":
        return <Clock className="h-4 w-4" />;
      case "pending":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Error Loading Cycle
        </h3>
        <p className="text-gray-500 mb-4">
          There was an error loading the performance cycle data.
        </p>
        <Button onClick={handleBack} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Cycles
        </Button>
      </div>
    );
  }

  if (!cycle) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Cycle Not Found
        </h3>
        <p className="text-gray-500 mb-4">
          The performance cycle you're looking for doesn't exist.
        </p>
        <Button onClick={handleBack} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Cycles
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
            icon={<ArrowLeft className="h-4 w-4" />}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{cycle.name}</h1>
            <p className="text-gray-600">{cycle.description}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <PermissionGate permission="performance" action="read">
            <Button variant="outline" icon={<Download className="h-4 w-4" />}>
              Export
            </Button>
          </PermissionGate>
          <PermissionGate permission="performance" action="read">
            <Button variant="outline" icon={<Share className="h-4 w-4" />}>
              Share
            </Button>
          </PermissionGate>
          <PermissionGate permission="performance" action="update">
            <Button onClick={handleEditCycle} icon={<Edit className="h-4 w-4" />}>
              Edit Cycle
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex items-center space-x-2">
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
            cycle.status
          )}`}
        >
          {getStatusIcon(cycle.status)}
          <span className="ml-2 capitalize">{cycle.status}</span>
        </span>
        <span className="text-sm text-gray-500">
          {cycle.startDate} - {cycle.endDate}
        </span>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-[#0F2942]" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Employees
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {cycle.totalEmployees}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {cycle.completedReviews}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">
                {cycle.pendingReviews}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Star className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Average Rating
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {cycle.averageRating}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {["overview", "stages", "departments", "activity", "goals"].map(
            (tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                  ? "border-brand text-brand"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                {tab}
              </button>
            )
          )}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 capitalize">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Progress Overview
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Overall Progress</span>
                    <span>
                      {cycle.totalEmployees > 0
                        ? Math.round(
                          (cycle.completedReviews / cycle.totalEmployees) *
                          100
                        )
                        : 0}
                      %
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-brand h-2 rounded-full"
                      style={{
                        width: `${cycle.totalEmployees > 0
                          ? Math.min(
                            100,
                            (cycle.completedReviews /
                              cycle.totalEmployees) *
                            100
                          )
                          : 0
                          }%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-600">
                      {cycle.completedReviews}
                    </p>
                    <p className="text-sm text-gray-600">Completed</p>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <Clock className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-yellow-600">
                      {cycle.pendingReviews}
                    </p>
                    <p className="text-sm text-gray-600">Pending</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Recent Activity
              </h3>
              <div className="space-y-3">
                {cycle.recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="p-1 bg-blue-100 rounded-full">
                      <Activity className="h-4 w-4 text-[#0F2942]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {activity.action}
                      </p>
                      <p className="text-sm text-gray-500">
                        {activity.employee} • {activity.manager}
                      </p>
                      <p className="text-xs text-gray-400">
                        {activity.timestamp}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "stages" && (
          <div className="bg-white rounded-lg border border-gray-200 capitalize">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Review Stages
              </h3>
              <div className="space-y-4">
                {cycle.stages.map((stage, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div
                        className={`p-2 rounded-full ${getStatusColor(
                          stage.status
                        )}`}
                      >
                        {getStatusIcon(stage.status)}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {stage.name}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {stage.completed} of {stage.participants} completed
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="w-32 bg-gray-200 rounded-full h-2 mb-1">
                        <div
                          className="bg-brand h-2 rounded-full"
                          style={{
                            width: `${stage.participants > 0
                              ? Math.min(
                                100,
                                (stage.completed / stage.participants) * 100
                              )
                              : 0
                              }%`,
                          }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-500">
                        {stage.participants > 0
                          ? Math.round(
                            (stage.completed / stage.participants) * 100
                          )
                          : 0}
                        %
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "departments" && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Department Performance
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 capitalize">
                  <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Department
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Total Employees
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Completed
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Average Rating
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Progress
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cycle.departments.map((dept, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {dept.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {dept.total}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {dept.completed}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Star className="h-4 w-4 text-yellow-400 mr-1" />
                            {dept.average}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-brand h-2 rounded-full"
                              style={{
                                width: `${(dept.completed / dept.total) * 100
                                  }%`,
                              }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "activity" && (
          <div className="bg-white rounded-lg border border-gray-200 capitalize">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Activity Timeline
              </h3>
              <div className="space-y-4">
                {cycle.recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-4">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <Activity className="h-4 w-4 text-[#0F2942]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {activity.action}
                      </p>
                      <p className="text-sm text-gray-500">
                        {activity.employee}
                      </p>
                      <p className="text-sm text-gray-500">
                        Manager: {activity.manager}
                      </p>
                      <p className="text-xs text-gray-400">
                        {activity.timestamp}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "goals" && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Cycle Goals
              </h3>
              {cycle.goals.length > 0 ? (
                <div className="space-y-4">
                  {cycle.goals.map((goal) => (
                    <div
                      key={goal.id}
                      className="p-4 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">
                          {goal.title}
                        </h4>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${goal.status === "on-track"
                            ? "bg-green-100 text-green-800"
                            : goal.status === "at-risk"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                            }`}
                        >
                          {goal.status.replace("-", " ")}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>Target: {goal.target}</span>
                        <span>Current: {goal.current}</span>
                      </div>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${goal.status === "on-track"
                            ? "bg-green-500"
                            : goal.status === "at-risk"
                              ? "bg-red-500"
                              : "bg-yellow-500"
                            }`}
                          style={{
                            width: `${Math.min(
                              100,
                              (parseFloat(goal.current) /
                                parseFloat(goal.target)) *
                              100
                            )}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Goals Set
                  </h3>
                  <p className="text-gray-500">
                    No goals have been defined for this performance cycle yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceCycleDetail;
